import React, { useState } from 'react'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import API from 'common/logic/API'
import { Button, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'
import * as Types from 'common/utils/Types'
import { Navigate } from 'react-router-dom'
import MonthOfYear from 'common/logic/MonthOfYear'

const TransferFundVariable = "month-transfer-last-fund"

interface Props {
    onSuccess: string | ((result: unknown) => void)
    static?: boolean
    month: MonthOfYear
    amount: number
}

function TransferBetweenMonthsWorkflow(props: Props) {

    const [redirect, setRedirect] = useState<string | null>(null)

    if (redirect) {
        return <Navigate to={redirect} replace />
    }

    const loadingStep = (_parameters: unknown) => {
        const dataToFetch: Record<string, any> = {
            "funds": API.get('/funds'),
            "lastFund": API.getVariable(TransferFundVariable, "int")
        }
        return API.multiple(dataToFetch)
            .then((r: any) => ({
                lastFund: r.lastFund.value,
                funds: r.funds.results,
            }))
    }

    const userInputStep = (_parameters: unknown, loading_result: unknown, _values: unknown, _errors: unknown, completion: (input: unknown) => void) => {
        const formEl = form(loading_result as any, completion)
        return { component: formEl, title: "Transfer offset to another month" }
    }

    const executionStep = (_parameters: unknown, _loading_result: unknown, user_input: unknown) => {
        const requests: Record<string, any> = [
            API.post("/movements/month-transfer", user_input),
            API.setVariable(TransferFundVariable, (user_input as any).fundId)
        ]
        return API.multiple(requests)
    }

    const completionCallback = (execution_result: unknown) => {
        if (Types.isString(props.onSuccess)) {
            setRedirect(props.onSuccess as string)
        } else {
            (props.onSuccess as (result: unknown) => void)(execution_result)
        }
    }

    function form(loading_results: any, completion: (input: unknown) => void) {
        const loaded_funds = loading_results.funds

        const toMonth = props.month.nextMonth()

        const parseForm = (values: any) => {
            const data = {
                reason: `Transfer from ${props.month.toString()} to ${values.toMonth}`,
                fundId: values.fund,
                fromMonth: props.month.toString(),
                toMonth: values.toMonth,
                amount: values.amount,
                notes: values.notes || null,
            }
            completion(data)
        }

        const schema = yup.object().shape({
            amount: yup.number().required(),
            toMonth: yup.string().required().matches(MonthOfYear.regex),
            fund: yup.number().required(),
            notes: yup.string().nullable(),
        })

        const funds = loaded_funds.filter((f: any) => f.usedForNewMovements && !f.deleted)
        const fund_options = funds.map((item: any) => <option value={item.id} key={`fund${item.id}`}>{item.name}</option>)

        const initialValues = {
            toMonth: toMonth.toString(),
            amount: props.amount.toFixed(2),
            fund: (loading_results.lastFund !== undefined) ? loading_results.lastFund : funds[0].id,
            notes: "",
        }

        return <>
            <Formik
                validationSchema={schema}
                onSubmit={parseForm}
                initialValues={initialValues}
            >
                {({
                    handleSubmit,
                    handleChange,
                    values,
                    errors,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>

                        <Form.Group controlId="formAmount">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control
                                type='text'
                                name='amount'
                                placeholder="-10.00"
                                value={values.amount}
                                onChange={handleChange}
                                isInvalid={!!errors.amount}
                            />
                            <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formDate">
                            <Form.Label>To month</Form.Label>
                            <Form.Control
                                type='text'
                                name='toMonth'
                                placeholder="2020-12-31"
                                value={values.toMonth}
                                onChange={handleChange}
                                isInvalid={!!errors.toMonth}
                            />
                            <Form.Control.Feedback type="invalid">{errors.toMonth}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formFund">
                            <Form.Label>Fund</Form.Label>
                            <Form.Control
                                as='select'
                                name='fund'
                                value={values.fund}
                                onChange={handleChange}
                                isInvalid={!!errors.fund}
                            >
                                {fund_options}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">{errors.fund}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="formNotes">
                            <Form.Label>Note</Form.Label>
                            <Form.Control
                                as='textarea'
                                name='notes'
                                value={values.notes}
                                onChange={handleChange}
                                isInvalid={!!errors.notes}
                            />
                            <Form.Control.Feedback type="invalid">{errors.notes}</Form.Control.Feedback>
                        </Form.Group>

                        <Button variant="primary" type="submit">Save</Button>

                    </Form>
                )}
            </Formik>
        </>
    }

    return <OperationWorkflowModal
        static={props.static}
        loadingStep={loadingStep}
        userInputStep={userInputStep}
        executionStep={executionStep}
        completionCallback={completionCallback}
    />
}

export default TransferBetweenMonthsWorkflow
